import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import { Plus, Video, ExternalLink, Layers, Clock } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { API_BASE } from "@/config/api"
import { useEntitlements } from "@/hooks/use-entitlements"
import { opsErrorMessage } from "@/lib/ops-error"
import { toast } from "sonner"

const API = API_BASE

const APPT_STATUSES = ["confirmed", "pending", "cancelled", "no_show", "completed"]

function isTelehealthLocation(location) {
  const loc = (location || "").toLowerCase()
  return loc.includes("telehealth") || loc.includes("telemedicin") || loc.includes("online")
}

/** Format Date → value for <input type="datetime-local"> in local TZ */
function toLocalInput(d) {
  const date = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(date.getTime())) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Parse datetime-local string as local time → ISO UTC for API */
function fromLocalInput(s) {
  if (!s) return new Date().toISOString()
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
}

function eventClassNames(arg) {
  const a = arg.event.extendedProps || {}
  const classes = ["appt-event"]
  const st = (a.status || "").toLowerCase()
  if (st === "cancelled") classes.push("appt-cancelled")
  else if (st === "no_show") classes.push("appt-noshow")
  else if (st === "pending") classes.push("appt-pending")
  else if (st === "completed") classes.push("appt-completed")
  if (isTelehealthLocation(a.location)) classes.push("appt-telehealth")
  return classes
}

export default function Appointments() {
  const { t } = useTranslation()
  const { hasFeature } = useEntitlements()
  const canResources = hasFeature("scheduling_resources")
  const canQueues = hasFeature("queues")
  const canNetwork = hasFeature("multi_location")
  const [items, setItems] = useState([])
  const [clients, setClients] = useState([])
  const [resources, setResources] = useState([])
  const [allSites, setAllSites] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [proposedSlots, setProposedSlots] = useState([])
  const [shiftFollowing, setShiftFollowing] = useState(false)
  const [form, setForm] = useState({
    client_id: "",
    type: "Therapy",
    start: "",
    end: "",
    status: "confirmed",
    location: "In-person",
    meeting_url: "",
    visit_kind: "",
    visit_address: "",
    resource_id: "",
  })
  const [params, setParams] = useSearchParams()
  const calRef = useRef(null)
  const openedFromQuery = useRef(false)

  const filterClientId = params.get("client") || ""
  const filterClient = clients.find((c) => c.id === filterClientId)

  const load = useCallback(() => {
    axios
      .get(`${API}/appointments`, { params: allSites ? { all_sites: true } : {} })
      .then((r) => setItems(r.data))
      .catch(() => {})
    axios.get(`${API}/clients`).then((r) => setClients(r.data)).catch(() => {})
  }, [allSites])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!canResources) {
      setResources([])
      return
    }
    axios
      .get(`${API}/scheduling/resources`, { params: allSites ? { all_sites: true } : {} })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data.resources || []
        setResources(list.filter((x) => x.active !== false))
      })
      .catch(() => setResources([]))
  }, [canResources, allSites])

  const resourceName = useCallback(
    (id) => {
      if (!id) return ""
      const r = resources.find((x) => x.id === id)
      return r?.name || ""
    },
    [resources],
  )

  const openNew = useCallback((start, end, clientId) => {
    const s = start || new Date()
    const e = end || new Date(s.getTime() + 50 * 60000)
    setEditing(null)
    setForm({
      client_id: clientId || filterClientId || "",
      type: "Therapy",
      start: toLocalInput(s),
      end: toLocalInput(e),
      status: "confirmed",
      location: "In-person",
      meeting_url: "",
      visit_kind: "",
      visit_address: "",
      resource_id: "",
    })
    setProposedSlots([])
    setEditOpen(true)
  }, [filterClientId])

  useEffect(() => {
    if (openedFromQuery.current) return
    const action = params.get("action")
    const client = params.get("client")
    if (action === "new" || client) {
      openedFromQuery.current = true
      openNew(undefined, undefined, client || "")
    }
  }, [params, openNew])

  const openEdit = (a) => {
    setEditing(a)
    setForm({
      client_id: a.client_id,
      type: a.type,
      start: toLocalInput(a.start),
      end: toLocalInput(a.end),
      status: a.status || "confirmed",
      location: a.location,
      meeting_url: a.meeting_url || "",
      visit_kind: a.visit_kind || "",
      visit_address: a.visit_address || "",
      resource_id: a.resource_id || "",
    })
    setProposedSlots([])
    setEditOpen(true)
  }

  const events = useMemo(
    () =>
      items.map((a) => {
        const dim = filterClientId && a.client_id !== filterClientId
        const room = resourceName(a.resource_id)
        let title = isTelehealthLocation(a.location) ? `📹 ${a.client_name}` : a.client_name
        if (room) title = `${title} · ${room}`
        return {
          id: a.id,
          title,
          start: a.start,
          end: a.end,
          extendedProps: a,
          classNames: dim ? ["appt-dimmed"] : undefined,
          opacity: dim ? 0.35 : 1,
        }
      }),
    [items, filterClientId, resourceName],
  )

  const proposeTimes = async () => {
    try {
      const { data } = await axios.post(`${API}/appointments/slots/propose`)
      setProposedSlots(data.slots || [])
      toast.success(t("app.appointments.toast.slotsProposed"))
    } catch {
      toast.error(t("app.appointments.toast.unavailable"))
    }
  }

  const applySlot = (slot) => {
    setForm({
      ...form,
      start: toLocalInput(slot.start),
      end: toLocalInput(slot.end),
    })
  }

  const save = async () => {
    const client = clients.find((c) => c.id === form.client_id)
    if (!client) return toast.error(t("app.appointments.toast.selectClient"))
    const payload = {
      client_id: client.id,
      client_name: client.name,
      type: form.type,
      start: fromLocalInput(form.start),
      end: fromLocalInput(form.end),
      status: form.status,
      location: form.location,
      meeting_url: form.meeting_url || null,
      visit_kind: form.visit_kind || "",
      visit_address: form.visit_address || "",
      resource_id: form.resource_id || null,
    }
    try {
      if (editing) {
        const { data } = await axios.patch(`${API}/appointments/${editing.id}`, payload)
        setItems((prev) => prev.map((a) => (a.id === data.id ? data : a)))
      } else {
        const { data } = await axios.post(`${API}/appointments`, payload)
        setItems((prev) => [...prev, data])
      }
      setEditOpen(false)
      toast.success(t("app.appointments.toast.saved"))
    } catch (e) {
      toast.error(opsErrorMessage(e, t, "app.appointments.toast.saveFailed"))
    }
  }

  const remove = async () => {
    if (!editing?.id) return
    if (!window.confirm(t("app.appointments.confirmDelete"))) return
    try {
      await axios.delete(`${API}/appointments/${editing.id}`)
      setItems((prev) => prev.filter((a) => a.id !== editing.id))
      setEditOpen(false)
      toast.success(t("app.appointments.toast.deleted"))
    } catch {
      toast.error(t("app.appointments.toast.deleteFailed"))
    }
  }

  const queueCheckIn = async () => {
    if (!editing?.id) return toast.error(t("app.appointments.toast.saveFirst"))
    try {
      await axios.post(`${API}/appointments/${editing.id}/queue-check-in`, {})
      toast.success(t("app.appointments.toast.queueCheckIn", { defaultValue: "Check-in la coadă reușit" }))
    } catch (e) {
      toast.error(opsErrorMessage(e, t, "app.appointments.toast.checkInFailed"))
    }
  }

  const onEventDrop = async (info) => {
    const start = info.event.start.toISOString()
    const end = info.event.end?.toISOString() || new Date(info.event.start.getTime() + 50 * 60000).toISOString()
    try {
      if (shiftFollowing) {
        await axios.post(`${API}/appointments/chain-reschedule`, {
          appointment_id: info.event.id,
          start,
          end,
          shift_following: true,
        })
        load()
      } else {
        const { data } = await axios.patch(`${API}/appointments/${info.event.id}`, { start, end })
        setItems((prev) => prev.map((a) => (a.id === data.id ? data : a)))
      }
      toast.success(t("app.appointments.toast.rescheduled"))
    } catch (e) {
      info.revert()
      toast.error(opsErrorMessage(e, t, "app.appointments.toast.moveFailed"))
    }
  }

  const checkInGps = () => {
    if (!editing?.id) return toast.error(t("app.appointments.toast.saveFirst"))
    if (!navigator.geolocation) return toast.error(t("app.appointments.toast.geolocationUnavailable"))
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await axios.post(`${API}/appointments/${editing.id}/check-in`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
          })
          toast.success(t("app.appointments.toast.checkInSaved"))
          load()
        } catch {
          toast.error(t("app.appointments.toast.checkInFailed"))
        }
      },
      () => toast.error(t("app.appointments.toast.locationDenied")),
      { enableHighAccuracy: true, timeout: 15000 },
    )
  }

  const syncCalendar = async () => {
    if (!editing?.id) return toast.error(t("app.appointments.toast.saveFirstShort"))
    try {
      await axios.post(`${API}/appointments/${editing.id}/sync-calendar`)
      toast.success(t("app.appointments.toast.googleCalendar"))
    } catch {
      toast.error(t("app.appointments.toast.connectGoogleInSettings"))
    }
  }

  const clearClientFilter = () => {
    const next = new URLSearchParams(params)
    next.delete("client")
    next.delete("action")
    setParams(next, { replace: true })
  }

  const statusLabel = (s) => t(`app.appointments.status.${s}`, { defaultValue: s })

  return (
    <AppLayout
      title={t("app.appointments.title")}
      actions={
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" className="rounded-full gap-1" asChild>
            <Link to="/app/waitlist">
              <Clock className="h-3.5 w-3.5" /> {t("app.appointments.waitlistLink")}
            </Link>
          </Button>
          {canResources && (
            <Button variant="outline" size="sm" className="rounded-full gap-1" asChild>
              <Link to="/app/scheduling/resources">
                <Layers className="h-3.5 w-3.5" /> {t("app.appointments.resourcesLink")}
              </Link>
            </Button>
          )}
          <Button size="sm" className="rounded-full" data-testid="cal-new" onClick={() => openNew()}>
            <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("app.appointments.new")}
          </Button>
        </div>
      }
    >
      <style>{`
        .appt-cancelled { opacity: 0.45; text-decoration: line-through; }
        .appt-noshow { background-color: hsl(var(--destructive) / 0.25) !important; border-color: hsl(var(--destructive)) !important; }
        .appt-pending { background-color: hsl(45 90% 50% / 0.25) !important; border-color: hsl(45 90% 40%) !important; }
        .appt-completed { opacity: 0.7; }
        .appt-telehealth { border-left: 3px solid hsl(var(--accent)) !important; }
        .appt-dimmed { opacity: 0.3 !important; filter: grayscale(0.4); }
      `}</style>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={shiftFollowing} onChange={(e) => setShiftFollowing(e.target.checked)} />
          {t("app.appointments.shiftChain")}
        </label>
        {canNetwork && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={allSites} onChange={(e) => setAllSites(e.target.checked)} />
            {t("app.ops.allSites", { defaultValue: "Toate locațiile" })}
          </label>
        )}
        {filterClient && (
          <span className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full border border-border bg-muted/40">
            {t("app.appointments.filteredBy", { name: filterClient.name })}
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={clearClientFilter}>
              ×
            </button>
          </span>
        )}
      </div>

      {!items.length && (
        <p className="text-sm text-muted-foreground mb-3" data-testid="cal-empty">
          {t("app.appointments.emptyWeek")}
        </p>
      )}

      <div className="rounded-2xl border border-border bg-card p-2 overflow-hidden">
        <FullCalendar
          ref={calRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{ left: "prev,next today", center: "title", right: "timeGridWeek,timeGridDay" }}
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          weekends
          editable
          droppable
          events={events}
          eventClassNames={eventClassNames}
          eventClick={(arg) => openEdit(arg.event.extendedProps)}
          dateClick={(arg) => openNew(arg.date)}
          eventDrop={onEventDrop}
          eventResize={onEventDrop}
          height="auto"
        />
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? t("app.appointments.edit") : t("app.appointments.newDialog")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("app.appointments.client")}</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger><SelectValue placeholder={t("app.appointments.client")} /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.client_id && (
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  <Link to={`/app/clients?open=${form.client_id}`} className="text-accent underline inline-flex items-center gap-0.5">
                    <ExternalLink className="h-3 w-3" /> {t("app.appointments.openClient")}
                  </Link>
                  <Link to={`/app/notes?client=${form.client_id}`} className="text-accent underline inline-flex items-center gap-0.5">
                    <ExternalLink className="h-3 w-3" /> {t("app.appointments.openNotes")}
                  </Link>
                  {editing?.id && (
                    <Link to={`/app/notes?appointment=${editing.id}`} className="text-accent underline inline-flex items-center gap-0.5">
                      <ExternalLink className="h-3 w-3" /> {t("app.appointments.sessionNote")}
                    </Link>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>{t("app.appointments.statusLabel")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {APPT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {canResources && (
              <div>
                <Label>{t("app.appointments.resource")}</Label>
                <Select
                  value={form.resource_id || "none"}
                  onValueChange={(v) => setForm({ ...form, resource_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder={t("app.appointments.resourceNone")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("app.appointments.resourceNone")}</SelectItem>
                    {resources.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} ({r.resource_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Link to="/app/scheduling/resources" className="text-[10px] text-accent underline mt-1 inline-block">
                  {t("app.appointments.manageResources")}
                </Link>
              </div>
            )}
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={proposeTimes}>
              {t("app.appointments.proposeSlots")}
            </Button>
            {proposedSlots.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {proposedSlots.map((s) => (
                  <button key={s.start} type="button" className="text-xs px-2 py-1 rounded-full border" onClick={() => applySlot(s)}>
                    {new Date(s.start).toLocaleString("ro-RO", { weekday: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </button>
                ))}
              </div>
            )}
            <div><Label>{t("app.appointments.type")}</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} /></div>
            <div><Label>{t("app.appointments.start")}</Label><Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
            <div><Label>{t("app.appointments.end")}</Label><Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
            <div>
              <Label>{t("app.appointments.location")}</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="In-person">{t("app.appointments.locationInPerson")}</SelectItem>
                  <SelectItem value="Telehealth">{t("app.appointments.locationTelehealth")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isTelehealthLocation(form.location) && (
              <div className="rounded-xl border border-border p-3 space-y-2 bg-muted/30">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Video className="h-4 w-4" />
                  {t("app.appointments.telehealthRoom")}
                </div>
                {form.meeting_url ? (
                  <p className="text-xs text-muted-foreground break-all">{form.meeting_url}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t("app.appointments.telehealthAutoLink")}</p>
                )}
                {editing?.id && (
                  <Button size="sm" className="rounded-full w-full" asChild>
                    <Link to={`/app/call/${editing.id}`}>{t("app.appointments.openConsultation")}</Link>
                  </Button>
                )}
              </div>
            )}
            <div>
              <Label>{t("app.appointments.visitKind")}</Label>
              <Select value={form.visit_kind || "office"} onValueChange={(v) => setForm({ ...form, visit_kind: v === "office" ? "" : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="office">{t("app.appointments.visitOffice")}</SelectItem>
                  <SelectItem value="home">{t("app.appointments.visitHome")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.visit_kind === "home") && (
              <div>
                <Label>{t("app.appointments.visitAddress")}</Label>
                <Input value={form.visit_address} onChange={(e) => setForm({ ...form, visit_address: e.target.value })} placeholder={t("app.appointments.visitAddressPlaceholder")} />
                <p className="text-[10px] text-muted-foreground mt-1">{t("app.appointments.visitGpsHint")}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 mt-4">
            {editing && form.visit_kind === "home" && (
              <Button variant="secondary" className="rounded-full w-full" type="button" onClick={checkInGps}>
                {t("app.appointments.checkInGps")}
              </Button>
            )}
            {editing && canQueues && (
              <Button variant="outline" className="rounded-full w-full" type="button" onClick={queueCheckIn}>
                {t("app.appointments.queueCheckIn", { defaultValue: "Check-in la coadă" })}
              </Button>
            )}
            <div className="flex gap-2">
              {editing && <Button variant="outline" className="rounded-full flex-1" type="button" onClick={syncCalendar}>{t("app.appointments.syncGoogle")}</Button>}
              <Button className="rounded-full flex-1" onClick={save}>{t("app.appointments.save")}</Button>
            </div>
            {editing && (
              <Button variant="destructive" className="rounded-full w-full" type="button" onClick={remove}>
                {t("app.appointments.delete")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
